/**
 * Railway wallet / GBUX purchase API (same-origin proxy → fleet).
 * SSOT: GrudgeBuilder server/routes/wallet.ts + exchange routes.
 */
import { authHeaders, getGrudgeToken, isLoggedIn } from "./grudge-auth";
import type { WalletPurchaseCurrency } from "@shared/fleet";
import { GBUX } from "@shared/fleet";

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: authHeaders(init?.headers),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      (data as { error?: string }).error || `Request failed ${res.status}`,
    );
  }
  return data as T;
}

export interface WalletOverview {
  gbuxBalance?: number | string;
  solBalance?: number | string;
  primaryWallet?: string | null;
  custodialWallet?: string | null;
  linkedWallets?: Array<{ address: string; provider: string }>;
  [key: string]: unknown;
}

export interface WalletConfig {
  network: string;
  rpcEndpoint: string;
  crossmintEnabled: boolean;
  gbuxMint: string;
  usdtMint: string;
  gbuxRateUsd: number;
  purchaseFeePercent: number;
}

export function getPublicWalletConfigFallback(): WalletConfig {
  return {
    network: "mainnet-beta",
    rpcEndpoint: "https://api.mainnet-beta.solana.com",
    crossmintEnabled: true,
    gbuxMint: GBUX.mint,
    usdtMint: GBUX.usdtMint,
    gbuxRateUsd: 0.001,
    purchaseFeePercent: 0.05,
  };
}

export async function fetchWalletConfig(): Promise<WalletConfig> {
  try {
    return await api<WalletConfig>("/api/wallet/config");
  } catch {
    return getPublicWalletConfigFallback();
  }
}

export async function fetchWalletOverview(): Promise<WalletOverview | null> {
  if (!isLoggedIn()) return null;
  try {
    const res = await fetch("/api/wallet/overview", {
      headers: authHeaders(),
    });
    // 401 = expired / missing fleet JWT — expected when only wallet is connected
    if (res.status === 401 || res.status === 403) return null;
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return null;
    return data as WalletOverview;
  } catch {
    return null;
  }
}

export async function linkWalletChallenge(
  walletAddress: string,
): Promise<{ message: string; walletAddress: string }> {
  return api("/api/wallet/link/challenge", {
    method: "POST",
    body: JSON.stringify({ walletAddress }),
  });
}

export async function linkWalletConfirm(params: {
  walletAddress: string;
  message: string;
  signature: string;
  provider: string;
  label?: string;
}): Promise<{ success: boolean; setPrimary?: boolean }> {
  return api("/api/wallet/link/confirm", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

/** Quote SOL or USDT → GBUX (USD-stable via USDT) */
export async function purchaseQuote(
  currency: WalletPurchaseCurrency,
  amount: number,
): Promise<{
  currency: string;
  amountIn: number;
  gbuxOut: number;
  fee?: number;
  rate?: number;
  [key: string]: unknown;
}> {
  return api("/api/wallet/purchase/quote", {
    method: "POST",
    body: JSON.stringify({ currency, amount }),
  });
}

export async function purchaseIntent(params: {
  currency: WalletPurchaseCurrency;
  amount: number;
  linkedWalletAddress?: string;
}): Promise<{
  purchaseId: string;
  treasuryAddress: string;
  currency: string;
  amountIn: number;
  gbuxOut: number;
  expiresAt: string;
  quote?: unknown;
}> {
  return api("/api/wallet/purchase/intent", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function purchaseConfirm(params: {
  purchaseId: string;
  txSignature: string;
}): Promise<{ success: boolean; credited?: number; gbuxBalance?: string }> {
  return api("/api/wallet/purchase/confirm", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

/** Account-ledger SOL↔GBUX swap (Railway exchange) */
export async function exchangeQuote(
  amount: number,
  direction: "sol-to-gbux" | "gbux-to-sol",
): Promise<Record<string, unknown>> {
  return api("/api/exchange/quote", {
    method: "POST",
    body: JSON.stringify({ amount, direction }),
  });
}

export async function exchangeSwap(
  amount: number,
  direction: "sol-to-gbux" | "gbux-to-sol",
): Promise<Record<string, unknown>> {
  return api("/api/exchange/swap", {
    method: "POST",
    body: JSON.stringify({ amount, direction }),
  });
}

/** On-chain SPL GBUX balance for a public key (edge or server). */
export async function fetchChainGbuxBalance(
  walletAddress: string,
): Promise<number> {
  const res = await fetch(`/api/user/profile/${walletAddress}`);
  if (!res.ok) return 0;
  const data = await res.json();
  return parseFloat(data?.user?.gbuxBalance ?? "0") || 0;
}

export function requireAuthOrThrow(): void {
  if (!getGrudgeToken()) {
    throw new Error("Sign in with Grudge ID to buy GBUX");
  }
}
